import { useRef, useState } from "react";
import { ArrowUpFromLine, ImagePlus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ImageLightbox } from "@/components/ui/image-lightbox";
import { cn } from "@/lib/utils";

type EntityCardImageFieldProps = {
  altText: string;
  currentImageUrl: string | null;
  generateActionLabel?: string | null;
  onImageLoadError?: () => void;
  onGenerateImage?: () => Promise<void>;
  onPickImage: (file: File) => void;
  onRemoveImage: () => void;
  stagedImageUrl: string | null;
};

export function EntityCardImageField({
  altText,
  currentImageUrl,
  generateActionLabel = null,
  onImageLoadError,
  onGenerateImage,
  onPickImage,
  onRemoveImage,
  stagedImageUrl,
}: EntityCardImageFieldProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const previewUrl = stagedImageUrl || currentImageUrl;
  const imageAlt = t("entities.media.alt", { title: altText });

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <p className="text-[1.05rem] font-semibold tracking-tight text-foreground">
          {t("entities.media.title")}
        </p>

        <input
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            if (file) {
              onPickImage(file);
            }
          }}
          type="file"
        />

        {previewUrl ? (
          <button
            className="group block w-full overflow-hidden rounded-[1.8rem] border border-[#e3d7c9] bg-[#fffdf8] transition-colors hover:border-clay/35"
            onClick={() => setLightboxOpen(true)}
            type="button"
          >
            <img
              alt={imageAlt}
              className="h-[22rem] w-full object-cover transition duration-300 group-hover:scale-[1.01]"
              onError={onImageLoadError}
              src={previewUrl}
            />
          </button>
        ) : (
          <button
            className={cn(
              "flex h-[22rem] w-full flex-col items-center justify-center gap-4 rounded-[1.8rem] border border-dashed border-[#ded1c2] bg-[#fffdf8] px-6 text-center transition-colors",
              "hover:border-clay/35 hover:bg-white",
            )}
            onClick={() => fileInputRef.current?.click()}
            type="button"
          >
            <ArrowUpFromLine className="h-11 w-11 text-[#a18f7c]" />
            <div className="space-y-1">
              <p className="text-[1.05rem] font-medium text-foreground">
                {t("entities.media.upload")}
              </p>
              <p className="text-sm text-muted-foreground">{t("entities.media.empty")}</p>
            </div>
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <button
          className="inline-flex items-center gap-2 font-medium text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => fileInputRef.current?.click()}
          type="button"
        >
          <ImagePlus className="h-4 w-4" />
          {previewUrl ? t("entities.media.replace") : t("entities.media.upload")}
        </button>
        {previewUrl ? (
          <button
            className="font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={onRemoveImage}
            type="button"
          >
            {t("entities.media.remove")}
          </button>
        ) : null}
        {onGenerateImage && generateActionLabel ? (
          <button
            className="inline-flex items-center gap-2 font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => void onGenerateImage()}
            type="button"
          >
            <Sparkles className="h-4 w-4" />
            {t(generateActionLabel)}
          </button>
        ) : null}
      </div>

      {stagedImageUrl ? <p className="text-sm text-muted-foreground">{t("entities.media.staged")}</p> : null}

      {onGenerateImage ? <p className="text-sm leading-6 text-muted-foreground">{t("entities.media.generateHint")}</p> : null}

      <ImageLightbox
        alt={imageAlt}
        imageUrl={previewUrl}
        onImageError={onImageLoadError}
        onOpenChange={setLightboxOpen}
        open={lightboxOpen}
        title={t("entities.media.lightboxTitle", { title: altText })}
      />
    </div>
  );
}
